import { contrast, FONT_OPTIONS, isHex, RADIUS_OPTIONS } from '@/lib/color';
import { ServiceError } from '../../contracts';
import type { AdminStoreService, AdminSystemService } from '../../adminContracts';
import { ctx, delay, saveAdmin } from './context';

const invalid = (m: string) => new ServiceError(m, 'INVALID');
const link = (v: string) => /^(\/|https?:\/\/)\S*$/.test(v);

export const adminStore: AdminStoreService = {
  async getSettings() { return delay(ctx('store.manage').data.store); },
  async saveSettings(s) {
    const { data: d } = ctx('store.manage');
    if (!s.name.trim()) throw invalid('Store name is required');
    if (!/^\S+@\S+\.\S+$/.test(s.email)) throw invalid('Enter a valid contact email');
    if (!/^[+\d][\d\s-]{7,15}$/.test(s.phone.trim())) throw invalid('Enter a valid phone number');
    if (!/^\d{6}$/.test(s.address.pincode)) throw invalid('Pincode must be 6 digits');
    if (s.shipping.freeThreshold < 0 || s.shipping.flatFee < 0) throw invalid('Shipping amounts cannot be negative');
    if (!(s.shipping.handlingDays >= 0 && s.shipping.handlingDays <= 30)) throw invalid('Handling time must be 0–30 days');
    if (!(s.tax.rate >= 0 && s.tax.rate <= 40)) throw invalid('Tax rate must be between 0% and 40%');
    if (s.tax.gstin && !/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d]Z[A-Z\d]$/.test(s.tax.gstin)) throw invalid('GSTIN format looks wrong (e.g. 29ABCDE1234F1Z5)');
    if (s.seo.title.length > 70 || s.seo.description.length > 170) throw invalid('SEO title max 70 characters, description max 170');
    if (s.payments.every((p) => !p.enabled)) throw invalid('Enable at least one payment method');
    d.store = s;
    d.name = s.name;
    saveAdmin();
    return delay(s, 600);
  },
  async getBranding() { return delay(ctx('store.manage').data.branding); },
  async saveBranding(b) {
    const { data: d } = ctx('store.manage');
    for (const [k, v] of [['Primary', b.primaryColor], ['Secondary', b.secondaryColor], ['Accent', b.accentColor]]) if (!isHex(v)) throw invalid(`${k} colour must be a hex value like #5b5bf0`);
    if (contrast(b.primaryColor, '#ffffff') < 3) throw invalid('Primary colour is too light: white button text would be unreadable. Pick a darker colour.');
    if (contrast(b.secondaryColor, '#ffffff') < 4.5) throw invalid('Secondary colour must be dark enough for white text (contrast 4.5:1).');
    if (!(FONT_OPTIONS as readonly string[]).includes(b.fontFamily)) throw invalid('Choose one of the supported fonts');
    for (const [k, v] of [['Logo', b.logoUrl], ['Favicon', b.faviconUrl]] as const) if (v && !/^(https?:\/\/|data:image\/)/.test(v)) throw invalid(`${k} must be an http(s) URL or an uploaded image`);
    d.branding = b;
    saveAdmin();
    return delay(b, 500);
  },
  async getTheme() { return delay(ctx('store.manage').data.theme); },
  async saveTheme(t) {
    const { data: d } = ctx('store.manage');
    if (!RADIUS_OPTIONS.some((r) => r.value === t.borderRadius)) throw invalid('Choose one of the offered corner styles');
    if (!['light', 'dark'].includes(t.defaultMode)) throw invalid('Invalid theme option');
    d.theme = t;
    saveAdmin();
    return delay(t, 400);
  },
  async getNavigation() { return delay(ctx('store.manage').data.navigation); },
  async saveNavigation(n) {
    const { data: d } = ctx('store.manage');
    const check = (label: string, to: string) => {
      if (!label.trim() || label.length > 24) throw invalid('Menu labels must be 1–24 characters');
      if (!link(to)) throw invalid(`"${label}": links must start with "/" or "https://" and contain no spaces`);
    };
    if (n.header.length > 8) throw invalid('The header menu supports up to 8 items');
    if (n.footer.length > 4) throw invalid('The footer supports up to 4 columns');
    n.header.forEach((i) => check(i.label, i.to));
    n.footer.forEach((g) => { if (!g.title.trim()) throw invalid('Footer columns need a title'); g.items.forEach((i) => check(i.label, i.to)); });
    if (!n.header.some((i) => i.enabled)) throw invalid('Keep at least one visible header item');
    const ids = [...n.header.map((i) => i.id), ...n.footer.flatMap((g) => [g.id, ...g.items.map((i) => i.id)])];
    if (new Set(ids).size !== ids.length) throw invalid('Duplicate menu item ids');
    d.navigation = n;
    saveAdmin();
    return delay(n, 500);
  },
};

export const adminSystem: AdminSystemService = {
  async get() {
    const { data: d } = ctx('settings.manage');
    return delay({ notifications: d.system.notifications, orderPrefix: d.system.orderPrefix, team: d.system.team, modules: { ...d.features } });
  },
  async saveNotifications(n, orderPrefix) {
    const { data: d } = ctx('settings.manage');
    if (!/^[A-Z]{2,5}$/.test(orderPrefix)) throw invalid('Order prefix must be 2–5 capital letters');
    d.system.notifications = n;
    d.system.orderPrefix = orderPrefix;
    saveAdmin();
    return delay({ notifications: n, orderPrefix, team: d.system.team, modules: { ...d.features } }, 400);
  },
};
